import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Collapse,
  Button,
  Badge,
  Divider } from
'@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  LocationOn as LocationIcon,
  Business as BuildingIcon,
  Layers as FloorIcon,
  MeetingRoom as RoomIcon,
  Home as UnitIcon,
  Public as SiteIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  UnfoldMore as ExpandAllIcon,
  UnfoldLess as CollapseAllIcon,
  Devices as DevicesIcon,
  Stairs as HallwayIcon,
  Bathroom as RestroomIcon,
  Inventory as StockIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { usePermissions } from '../../contexts/PermissionContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { useSiteFilter } from '../../contexts/SiteFilterContext';
import { useIsMobile } from '../../hooks/useResponsive';
import locationService from '../../services/locationService';
import { LOCATION_TYPES } from '../../config/constants';
import EnhancedSearchBar from '../LocationSearch/EnhancedSearchBar';
import QuickFilters from '../LocationSearch/QuickFilters';
import LabelManager from '../LocationSearch/LabelManager';
import LabelStorage from '../../utils/labelStorage';
import { parseSearchQuery, filterLocationTree, buildDeviceCountMap } from '../../utils/advancedLocationSearch';
import { loadFilter, saveFilter, FILTER_KEYS } from '../../utils/filterPersistence';

// Styled components for UniFi-like appearance
const StyledTreeContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}));

const StyledTreeHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const StyledTreeBody = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2)
}));

const StyledTreeNode = styled(Box)(({ theme, depth, selected }) => ({
  marginLeft: theme.spacing(depth * 3),
  padding: theme.spacing(1, 1.5),
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.spacing(0.5),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  border: `1px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.light
  }
}));

const StyledNodeContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

const StyledNodeInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1
}));

const StyledNodeActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  opacity: 0,
  transition: 'opacity 0.2s ease',
  '.MuiBox-root:hover &': {
    opacity: 1
  }
}));

// Location type icons mapping
const getLocationIcon = (locationType, friendlyName) => {
  // Check friendlyName for additional hints if type is not specific enough
  const nameLower = friendlyName?.toLowerCase() || '';

  // Type-based mapping
  if (locationType === 1) return <SiteIcon />;
  if (locationType === 2) return <BuildingIcon />;
  if (locationType === 3 || nameLower.includes('verdieping')) return <FloorIcon />;
  if (locationType === 4) return <UnitIcon />;
  if (locationType === 5 || nameLower.includes('kamer')) return <RoomIcon />;
  if (locationType === 6 || nameLower.includes('gang') || nameLower.includes('hal')) return <HallwayIcon />;
  if (locationType === 7 || nameLower.includes('badkamer') || nameLower.includes('toilet') || nameLower.includes('wc')) return <RestroomIcon />;
  if (locationType === 8 || nameLower.includes('voorraad') || nameLower.includes('magazijn')) return <StockIcon />;

  // Additional name-based detection
  if (nameLower.includes('aw ')) return <UnitIcon />;

  // Default icon
  return <LocationIcon />;
};

const HierarchicalLocationView = ({
  onLocationSelect,
  onLocationEdit,
  onLocationDelete,
  onLocationAdd,
  selectedLocationId,
  refreshTrigger
}) => {
  const { t } = useThemeSettings();
  const isMobile = useIsMobile();
  const { selectedSiteId, siteLocationIds, hasSiteFilter } = useSiteFilter();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(() =>
    loadFilter(FILTER_KEYS.LOCATION.SEARCH, '')
  );
  const [activeQuickFilters, setActiveQuickFilters] = useState(() =>
    loadFilter(FILTER_KEYS.LOCATION.ACTIVE_FILTERS, [])
  );
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedPath, setSelectedPath] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [contextLocation, setContextLocation] = useState(null);
  const { hasPermission } = usePermissions();

  // Load locations on mount, refresh, or site filter change
  useEffect(() => {
    loadLocations();
  }, [refreshTrigger, selectedSiteId, siteLocationIds, hasSiteFilter]);

  // Persist search term to localStorage
  useEffect(() => {
    saveFilter(FILTER_KEYS.LOCATION.SEARCH, searchTerm);
  }, [searchTerm]);

  // Persist active filters to localStorage
  useEffect(() => {
    saveFilter(FILTER_KEYS.LOCATION.ACTIVE_FILTERS, activeQuickFilters);
  }, [activeQuickFilters]);

  const buildLocationHierarchy = (locations) => {
    if (!Array.isArray(locations) || locations.length === 0) return [];

    // Create a map for quick lookup
    const locationMap = {};
    locations.forEach((loc) => {
      const locId = loc.id || loc._id || loc.location_UUID;
      locationMap[locId] = { ...loc, children: [] };
    });

    // Build the tree structure
    const roots = [];
    locations.forEach((loc) => {
      const locId = loc.id || loc._id || loc.location_UUID;
      const parentId = loc.parentLocation || loc.parent_id || loc.parentId;

      if (parentId && locationMap[parentId]) {
        // Add as child to parent
        locationMap[parentId].children.push(locationMap[locId]);
      } else {
        // No parent or parent not found, add as root
        roots.push(locationMap[locId]);
      }
    });

    return roots;
  };

  const loadLocations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Wait for site filter to be ready if active
      // If hasSiteFilter is true but siteLocationIds is not yet loaded, don't fetch yet
      if (hasSiteFilter && !Array.isArray(siteLocationIds)) {
        console.log('[HierarchicalLocationView] Site filter active but siteLocationIds not loaded yet, waiting...');
        setLoading(false);
        return; // Will re-trigger when siteLocationIds becomes available
      }

      const response = await locationService.getAllLocations({ useCache: !hasSiteFilter, forceRefresh: hasSiteFilter });

      // Handle different response structures (same as used in locationService.getStatistics)
      let flatLocations = response?.data?.locations ||
                           response?.data?.data ||
                           response?.data?.docs ||
                           response?.data ||
                           [];

      // Filter by site if a site is selected
      console.log(`[HierarchicalLocationView] Site filter check - hasSiteFilter: ${hasSiteFilter}, siteLocationIds: ${siteLocationIds ? siteLocationIds.length : 'null'}, selectedSiteId: ${selectedSiteId}`);
      if (hasSiteFilter && Array.isArray(siteLocationIds)) {
        const beforeCount = flatLocations.length;
        flatLocations = flatLocations.filter(loc => {
          const locId = loc.id || loc._id || loc.location_UUID;
          return siteLocationIds.includes(Number(locId)) || siteLocationIds.includes(String(locId));
        });
        console.log(`[HierarchicalLocationView] Filtered from ${beforeCount} to ${flatLocations.length} locations for selected site`);
      }

      // Build hierarchical structure
      const hierarchicalLocations = buildLocationHierarchy(flatLocations);

      setLocations(hierarchicalLocations);

      // Auto-expand first level if we have data
      if (hierarchicalLocations.length > 0) {
        const firstLevelIds = hierarchicalLocations.
        filter((loc) => loc && (loc.id || loc._id || loc.location_UUID)).
        map((loc) => loc.id || loc._id || loc.location_UUID);
        setExpandedNodes(new Set(firstLevelIds));
      }
    } catch (err) {
      console.error('Error loading locations:', err);
      setError('Failed to load locations. Please check your connection and try again.');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  // Toggle node expansion
  const toggleNode = (nodeId, event) => {
    event.stopPropagation();
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // Expand all nodes
  const expandAll = () => {
    const allNodeIds = [];
    const collectIds = (nodes) => {
      nodes.forEach((node) => {
        if (node) {
          const nodeId = node.id || node._id;
          if (nodeId) {
            allNodeIds.push(nodeId);
          }
          if (node.children && node.children.length > 0) {
            collectIds(node.children);
          }
        }
      });
    };
    collectIds(locations);
    setExpandedNodes(new Set(allNodeIds));
  };

  // Collapse all nodes
  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  // Handle location selection
  const handleLocationSelect = (location, path) => {
    setSelectedPath(path);
    if (onLocationSelect) {
      onLocationSelect(location);
    }
  };

  // Handle context menu
  const handleContextMenu = (event, location) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setContextLocation(location);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setContextLocation(null);
  };

  // Enhanced filtering with search operators and labels
  const filteredLocations = useMemo(() => {
    // Ensure searchTerm is a string before calling trim()
    const term = typeof searchTerm === 'string' ? searchTerm : '';
    if (!term || term.trim().length === 0) {
      return locations;
    }

    const searchCriteria = parseSearchQuery(term);
    const deviceCounts = buildDeviceCountMap(locations);

    return filterLocationTree(locations, searchCriteria, deviceCounts);
  }, [locations, searchTerm]);

  // Handle quick filter selection
  const handleQuickFilterClick = (filter) => {
    // Toggle filter - if already active, remove it; otherwise add it
    if (activeQuickFilters.includes(filter.id)) {
      // Remove filter
      setActiveQuickFilters(prev => prev.filter(id => id !== filter.id));
      setSearchTerm('');
    } else {
      // Add filter and set search term
      setActiveQuickFilters([filter.id]); // Only one filter active at a time for simplicity
      setSearchTerm(filter.filter);
    }
  };

  // Render location node
  const renderNode = (node, depth = 0, path = []) => {
    if (!node) {
      console.warn('Attempted to render null/undefined node');
      return null;
    }

    const nodeId = node.id || node._id || node.location_UUID;
    const nodeName = node.friendlyName || node.name || node.title || 'Unnamed Location';
    const nodeLabels = LabelStorage.getLabelsForLocation(nodeId);

    const isExpanded = expandedNodes.has(nodeId);
    const isSelected = selectedLocationId === nodeId;
    const hasChildren = node.children && node.children.length > 0;
    const currentPath = [...path, { id: nodeId, name: nodeName }];

    return (
      <Box key={nodeId}>
        <StyledTreeNode
          depth={depth}
          selected={isSelected}
          onClick={() => handleLocationSelect(node, currentPath)}
          onContextMenu={(e) => handleContextMenu(e, node)}>
          
          <StyledNodeContent>
            <StyledNodeInfo>
              {hasChildren &&
              <IconButton
                size="small"
                onClick={(e) => toggleNode(nodeId, e)}
                sx={{ mr: 0.5 }}>
                
                  {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              }
              {!hasChildren && <Box sx={{ width: 32 }} />}

              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                {getLocationIcon(node.locationType, nodeName)}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body1" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                    {nodeName}
                  </Typography>
                  {nodeLabels.length > 0 && nodeLabels.slice(0, 2).map(label => (
                    <Chip
                      key={label.id}
                      label={label.name}
                      size="small"
                      sx={{
                        height: { xs: 24, sm: 20, md: 18 },
                        fontSize: { xs: '0.75rem', sm: '0.6875rem', md: '0.625rem' },
                        bgcolor: label.color,
                        color: '#fff',
                        '& .MuiChip-label': { px: { xs: 1, sm: 0.75 } }
                      }}
                    />
                  ))}
                  {nodeLabels.length > 2 && (
                    <Chip
                      label={`+${nodeLabels.length - 2}`}
                      size="small"
                      sx={{
                        height: { xs: 24, sm: 20, md: 18 },
                        fontSize: { xs: '0.75rem', sm: '0.6875rem', md: '0.625rem' },
                        '& .MuiChip-label': { px: { xs: 1, sm: 0.75 } }
                      }}
                    />
                  )}
                </Box>
                {node.description &&
                <Typography variant="caption" color="text.secondary">
                    {node.description}
                  </Typography>
                }
              </Box>

              {node.deviceCount > 0 &&
              <Badge badgeContent={node.deviceCount} color="primary" sx={{ mr: 2 }}>
                  <DevicesIcon fontSize="small" color="action" />
                </Badge>
              }
            </StyledNodeInfo>
            
            <StyledNodeActions>
              {hasPermission('locations.create') &&
              <Tooltip title={t ? t("HierarchicalLocationView.add_sublocation") : "Add sublocation"}>
                  <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLocationAdd && onLocationAdd(node);
                  }}>
                  
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              
              {hasPermission('locations.edit') &&
              <Tooltip title={t ? t("HierarchicalLocationView.edit_location") : "Edit location"}>
                  <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLocationEdit && onLocationEdit(node);
                  }}>
                  
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              
              {hasPermission('locations.delete') && !hasChildren && node.deviceCount === 0 &&
              <Tooltip title={t ? t("HierarchicalLocationView.delete_location") : "Delete location"}>
                  <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLocationDelete && onLocationDelete(node);
                  }}>
                  
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              
              <IconButton
                size="small"
                onClick={(e) => handleContextMenu(e, node)}>
                
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </StyledNodeActions>
          </StyledNodeContent>
        </StyledTreeNode>
        
        {hasChildren && isExpanded &&
        <Collapse in={isExpanded}>
            {node.children.map((child) => renderNode(child, depth + 1, currentPath))}
          </Collapse>
        }
      </Box>);

  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>);

  }

  return (
    <StyledTreeContainer elevation={1}>
      <StyledTreeHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <EnhancedSearchBar
            onSearch={(value) => setSearchTerm(value)}
            placeholder={t ? t("LocationManagement.search_locations") : "Search locations..."}
            autoFocus={false}
            initialValue={searchTerm}
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t ? t("HierarchicalLocationView.expand_all") : "Expand all"}>
            <IconButton size="small" onClick={expandAll}>
              <ExpandAllIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={t ? t("HierarchicalLocationView.collapse_all") : "Collapse all"}>
            <IconButton size="small" onClick={collapseAll}>
              <CollapseAllIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={t ? t("DeviceStatusDashboard.refresh") : "Refresh"}>
            <IconButton size="small" onClick={loadLocations}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          {hasPermission('locations.create') &&
          <Tooltip title={t ? t("HierarchicalLocationView.add_location") : "Add location"}>  
            <IconButton size="small" onClick={() => onLocationAdd && onLocationAdd(null)}>
              <AddIcon />
            </IconButton>
          </Tooltip>
          }
        </Box>
      </StyledTreeHeader>
      
      {/* Quick Filters */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <QuickFilters
          onFilterClick={handleQuickFilterClick}
          activeFilters={activeQuickFilters}
        />
      </Box>

      {selectedPath.length > 0 &&
      <Box sx={{ px: 2, py: 1, backgroundColor: 'grey.50' }}>
          <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />}>
            {selectedPath.map((item, index) =>
          <Link
            key={item.id}
            color={index === selectedPath.length - 1 ? 'text.primary' : 'inherit'}
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              const location = locations.find((loc) => loc.id === item.id);
              if (location) handleLocationSelect(location, selectedPath.slice(0, index + 1));
            }}>

                {item.name}
              </Link>
          )}
          </Breadcrumbs>
        </Box>
      }
      
      <StyledTreeBody>
        {error &&
        <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        }
        
        {filteredLocations.length === 0 ?
        <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {searchTerm ? 'No locations found matching your search' : 'No locations available'}
            </Typography>
          </Box> :

        <Box>
            {filteredLocations.map((location) => renderNode(location, 0, []))}
          </Box>
        }
      </StyledTreeBody>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}>
        
        <MenuItem onClick={() => {
          onLocationEdit && onLocationEdit(contextLocation);
          handleCloseMenu();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />{t ? t("LocationManagement.edit_location") : "Edit Location"}

        </MenuItem>
        
        <MenuItem onClick={() => {
          onLocationAdd && onLocationAdd(contextLocation);
          handleCloseMenu();
        }}>
          <AddIcon fontSize="small" sx={{ mr: 1 }} />{t ? t("HierarchicalLocationView.add_sublocation_1") : "Add Sublocation"}

        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={() => {
          handleCloseMenu();
        }}>{t ? t("LocationManagement.view_details") : "View Details"}

        </MenuItem>
        
        {contextLocation && !contextLocation.children?.length && contextLocation.deviceCount === 0 &&
        <>
            <Divider />
            <MenuItem
            onClick={() => {
              onLocationDelete && onLocationDelete(contextLocation);
              handleCloseMenu();
            }}
            sx={{ color: 'error.main' }}>
            
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />{t ? t("LocationManagement.delete_location") : "Delete Location"}

          </MenuItem>
          </>
        }
      </Menu>
    </StyledTreeContainer>);

};

export default HierarchicalLocationView;